// Einstellungen → Lernschleife (Phase 3, Admin): Vorschläge für neue oder geänderte Handbuch-Kapitel aus
// dem Vergleich Bob-Entwurf ↔ gesendete Fassung und aus wiederkehrenden Anweisungen.
import { useCallback, useEffect, useState } from 'react';
import { api, type LernWoche } from '../lib/api.ts';
import { toast } from './Toast.tsx';

const ART: Record<string, string> = {
  neues_kapitel: 'Neues Handbuch-Kapitel',
  kapitel_aendern: 'Kapitel ändern',
  ton: 'Ton und Form',
  baustein: 'Textbaustein',
};

export function Lernschleife() {
  const [data, setData] = useState<{
    laeuft: boolean;
    offeneFaelle: number;
    wochen: LernWoche[];
  } | null>(null);
  const [sel, setSel] = useState(0);
  const load = useCallback(
    () =>
      api
        .lernschleife()
        .then(setData)
        .catch((e: Error) => toast(e.message)),
    [],
  );
  useEffect(() => void load(), [load]);
  useEffect(() => {
    if (!data?.laeuft) return;
    const t = setInterval(() => void load(), 5000);
    return () => clearInterval(t);
  }, [data?.laeuft, load]);
  if (!data) return <div className="muted">wird geladen …</div>;
  const w = data.wochen[sel];
  const kopieren = (v: LernWoche['vorschlaege'][number]) =>
    void navigator.clipboard
      .writeText(
        `${v.titel}${v.kapitel ? ` (Kapitel ${v.kapitel})` : ''}\n\n${v.text}\n\nBegründung: ${v.begruendung}`,
      )
      .then(() =>
        toast(
          'In die Zwischenablage kopiert – ins Handbuch (_bob-handbuch.js) übernehmen und STAND setzen',
        ),
      );
  return (
    <div className="fw">
      <div className="fw-head">
        <select
          value={sel}
          onChange={(e) => setSel(Number(e.target.value))}
          disabled={!data.wochen.length}
        >
          {data.wochen.length === 0 && <option>noch keine Auswertung</option>}
          {data.wochen.map((x, i) => (
            <option key={x.woche} value={i}>
              {x.woche} · {x.faelle} Fälle, {x.starkGeaendert} stark geändert
            </option>
          ))}
        </select>
        <button
          className="btn sm"
          disabled={data.laeuft}
          onClick={() =>
            void api
              .lernschleifeAuswerten()
              .then(() => {
                toast('Bob wertet die letzten 7 Tage aus …');
                void load();
              })
              .catch((e: Error) => toast(e.message))
          }
        >
          {data.laeuft ? 'Bob wertet aus …' : `Jetzt auswerten (${data.offeneFaelle} neue Fälle)`}
        </button>
      </div>
      {w && w.vorschlaege.length === 0 && (
        <div className="muted">Keine Vorschläge in dieser Woche.</div>
      )}
      {w?.vorschlaege.map((v, i) => (
        <div key={i} className="fw-thema">
          <div className="fw-t1">
            <span className="pill mut">{ART[v.art] ?? v.art}</span>
            <b>{v.titel}</b>
            {v.kapitel && (
              <span className="muted" style={{ fontSize: 12 }}>
                → {v.kapitel}
              </span>
            )}
            <span className="sp" />
            <button className="btn sm" onClick={() => kopieren(v)}>
              Kopieren
            </button>
          </div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{v.text}</div>
          <div className="muted" style={{ fontSize: 12 }}>
            {v.begruendung}
            {v.beispiele.length > 0 && (
              <>
                {' · '}
                {v.beispiele.map((b, j) => (
                  <span key={j}>
                    {j > 0 && ', '}
                    {b.threadId ? (
                      <a href={`/t/${b.threadId}`} target="_blank" rel="noreferrer">
                        Thread {b.threadId}
                      </a>
                    ) : (
                      'Thread'
                    )}
                    {b.anweisung ? ` („${b.anweisung}“)` : ''}
                  </span>
                ))}
              </>
            )}
          </div>
        </div>
      ))}
      {w && w.haeufigeAnweisungen.length > 0 && (
        <div className="muted" style={{ fontSize: 12.5 }}>
          Wiederkehrende Anweisungen:{' '}
          {w.haeufigeAnweisungen.map((a) => `„${a.anweisung}“ (${a.anzahl}×)`).join(', ')}
        </div>
      )}
      {w && (
        <div className="muted" style={{ fontSize: 11.5 }}>
          Ausgewertet {new Date(w.erzeugtAm).toLocaleString('de-DE')} · {w.kostenEur.toFixed(2)} €.
          Übernehmen tut Till in `_bob-handbuch.js` (STAND setzen).
        </div>
      )}
    </div>
  );
}
